import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import classNames from 'classnames'
import { TrackEvent, useTracking } from './common/tracking'
import { useGetUsersQuery } from '../../../store/user/user'
import { useAppSelector } from '../../../store/hooks'
import { selectAccountId, selectSafeAuthenticatedUser } from '../../../store/auth/authSlice'
import { Col, Container, Form, Modal as BootstrapModal, OverlayTrigger, Row, Spinner } from 'react-bootstrap'
import { Modal } from '../../../components/Modal/Modal'
import { useGetUserAllocationQuery, useUpdateUserAllocationMutation } from '../../../store/media/media'
import { Allocation } from '@zocks-communications/client-sdk-js'
import { CustomDropdownItemOption, DropDownItemOptionComponentProps, SelectDropdown, SelectOption } from '../../../components/SelectDropdown/SelectDropdown'
import { RoomSessionContext } from './models'
import { UserTag } from './UserTag'
import { getUserDisplayName } from './common/aifeatures/utils'
import { getRoomTrackingDetails } from './tracking'
import styled from 'styled-components'
import { Button } from '../../../components/Button/Button'
import { AddContactFormValues, AddContactModal } from '../../../views/Contacts/AddContactModal'
import { User } from '../../../models/User'
import { MissingScopeAction } from '../../../components/ScopedBlock/models'
import { ScopedBlock } from '../../../components/ScopedBlock/ScopedBlock'
import useFocus from '../../../common/useFocus'
import { parseMetadata } from './MeetingSummary/utils'
import { SpeakingDistributionIcon } from './MeetingSummary/ConfigureAssignment/SpeakingDistributionIcon'
import { ZocksAIAssistantTagVertical } from '../../../components/Tag/Tag'
import BootstrapTooltip from 'react-bootstrap/Tooltip'
import { useLazyGetAiAgentResultQuery } from '../../../store/ai/aiAgentResult'
import { AIAgentType } from '../../../models/AIAgent'
import { OverlayInjectedProps } from 'react-bootstrap/Overlay'
import { ContactInformation, ContactInformationByUserId, ContactInformationExecutionResult } from '../../../models/ContactInformation'
import { useExecuteAiAgentMutation } from '../../../store/ai/aiAgent'
import './AllocationModal.scss'

const AlertContainer = styled.div`
  display: flex;
  padding: 1rem;
  align-items: center;
  border-radius: 0.25rem;
  background: var(--zocks-gray-900, #212529);
  gap: 1rem;
  color: white;
`

export const AllocationModal: React.FC<{
  show: boolean
  roomId: string
  sessionId: string
  onSaveNewAllocation: () => void
  onCancelNewAllocation: () => void
}> = ({ show, roomId, sessionId, onSaveNewAllocation, onCancelNewAllocation }) => {
  const { tracked } = useTracking()
  const roomSession = useContext(RoomSessionContext)
  const accountId = useAppSelector(selectAccountId)
  const { id: currentUserId } = useAppSelector(selectSafeAuthenticatedUser)

  const { data: allocations, isFetching: userAllocationLoading, error: userAllocationError } = useGetUserAllocationQuery({ accountId, roomId, sessionId })

  const [updateUserAllocation, { isLoading: updateUserAllocationLoading }] = useUpdateUserAllocationMutation()

  const [triggerGetAiAgentResult] = useLazyGetAiAgentResultQuery()
  const [triggerExecuteAiAgent] = useExecuteAiAgentMutation()
  const [contactInformationIsLoading, setContactInformationIsLoading] = useState(false)

  const [newAllocations, setNewAllocations] = useState<Allocation[]>([])
  const [showAddContactModal, setShowAddContactModal] = useState<boolean>(false)
  const [addContactForAllocation, setAddContactForAllocation] = useState<Allocation | null>(null)
  const [addContactValues, setAddContactValues] = useState<Partial<AddContactFormValues>>()
  const [contactCreatedForAllocations, setContactCreatedForAllocations] = useState<Allocation[]>([])
  const [contactCreatedLoadingForAllocation, setContactCreatedLoadingForAllocation] = useState<Allocation | null>(null)
  const [contactInformationByUserId, setContactInformationByUserId] = useState<ContactInformationByUserId | null>(null)

  const rawSpeakingDistribution = useMemo(() => {
    if (!roomSession) {
      return null
    }
    const metadata = parseMetadata(roomSession.metadata)
    const { rawSpeakingDistribution } = metadata
    return rawSpeakingDistribution ?? null
  }, [roomSession])

  const getExistingContactInformation = useCallback(
    () =>
      triggerGetAiAgentResult(
        {
          accountId,
          type: AIAgentType.CONTACT_INFORMATION,
          resultKey: `${roomSession?.roomId}|${roomSession?.id}`,
        },
        false,
      ).unwrap(),
    [accountId, roomSession, triggerGetAiAgentResult],
  )

  const runExecuteContactInformation = useCallback(
    () =>
      triggerExecuteAiAgent({
        accountId,
        payload: {
          type: AIAgentType.CONTACT_INFORMATION,
          context: {
            roomId,
            sessionId,
          },
        },
      }).unwrap() as Promise<ContactInformationExecutionResult>,
    [accountId, roomId, sessionId, triggerExecuteAiAgent],
  )

  const executeContactInformation = useCallback(async () => {
    setContactInformationIsLoading(true)
    setContactInformationByUserId(null)
    try {
      const contactInformationExecutionResult = await runExecuteContactInformation()
      const { result } = contactInformationExecutionResult
      setContactInformationByUserId(result)
    } finally {
      setContactInformationIsLoading(false)
    }
  }, [runExecuteContactInformation])

  useEffect(() => {
    const getContactInformation = async () => {
      const existingContactInformation = await getExistingContactInformation()
      if (existingContactInformation) {
        const { result } = existingContactInformation
        const resultWithValues = Object.fromEntries(Object.entries(result).filter(([_, v]) => v != null))
        setContactInformationByUserId(resultWithValues)
      }
    }
    getContactInformation()
  }, [getExistingContactInformation])

  useEffect(() => {
    if (!allocations) {
      return
    }
    setNewAllocations(
      allocations.map((allocation) => ({
        ...allocation,
        userType: getUserTypeForAllocation(allocation),
      })),
    )
  }, [allocations])

  const allocationsChanged = () => {
    if (!allocations) {
      return false
    }

    return allocations.some((allocation) => {
      const newAllocation = newAllocations.find((a) => a.transcriptDesignation === allocation.transcriptDesignation)
      return (
        !newAllocation ||
        (newAllocation.userId && newAllocation.userId !== allocation.userId) ||
        (newAllocation.name && newAllocation.name !== getUserNameForAllocation(allocation))
      )
    })
  }

  const allocationExistsWithEmptyName = newAllocations.some((a) => a.name !== null && !a.name)

  const saveNewAllocation = async () => {
    if (allocationsChanged()) {
      updateNewUserAllocation()
    } else {
      onCancelNewAllocation()
    }
  }

  const _updateNewUserAllocation = () => {
    updateUserAllocation({
      accountId,
      roomId,
      sessionId,
      body: {
        allocations: newAllocations,
        updateOptions: {
          allocatedTranscriptionMetadata: getAllocatedTranscriptionMetadata(),
          deleteResults: true,
        },
      },
    }).then(onSaveNewAllocation)
  }

  const updateNewUserAllocation = tracked(_updateNewUserAllocation, {
    eventName: TrackEvent.NEW_ALLOCATION_SAVED,
    eventDetails: {
      ...getRoomTrackingDetails(roomSession),
      newAllocations: newAllocations.map((newAllocation) => {
        return {
          userId: newAllocation.userId,
          userType: newAllocation.userType,
          ...(newAllocation.roles.length && { roles: newAllocation.roles }),
          name: newAllocation.name,
          transcriptDesignation: newAllocation.transcriptDesignation,
          email: newAllocation.email,
        }
      }),
    },
  })

  const getAllocatedTranscriptionMetadata = () => {
    const { source } = getRoomSessionMetadata()
    return source === 'import' ? { allocatedOnImport: true } : {}
  }

  const getRoomSessionMetadata = () => {
    try {
      return roomSession ? JSON.parse(roomSession.metadata) : {}
    } catch (e) {
      return {}
    }
  }

  const modalHeader = (
    <BootstrapModal.Title>
      <div>Participant Assignment</div>
    </BootstrapModal.Title>
  )

  const onAllocationUserSelected = useCallback((allocation: Allocation, selectedUser?: User) => {
    const { id = null, name = null, email = null, type = 'anonymous', roles = [] } = selectedUser ?? {}
    const { transcriptDesignation } = allocation
    setNewAllocations((newAllocations) => {
      return newAllocations.map((newAllocation) =>
        getAllocationId(newAllocation) !== getAllocationId(allocation)
          ? newAllocation
          : { transcriptDesignation, userId: id, name, email, userType: type, roles: roles.map((r) => r.id) },
      )
    })
  }, [])

  const handleAddContactModalSave = (newExternalUser?: User) => {
    setShowAddContactModal(false)
    if (!addContactForAllocation || !newExternalUser) {
      return
    }
    setContactCreatedLoadingForAllocation(addContactForAllocation)
    onAllocationUserSelected(addContactForAllocation, newExternalUser)
    setContactCreatedForAllocations((contactCreatedForAllocations) => [...contactCreatedForAllocations, addContactForAllocation])
    setContactCreatedLoadingForAllocation(null)
  }

  const handleAddContactModalCancel = () => {
    setShowAddContactModal(false)
  }

  const handleAddContactClicked = (allocation: Allocation) => {
    setAddContactForAllocation(allocation)
    const newAllocation = newAllocations.find((newAll) => getAllocationId(newAll) === getAllocationId(allocation))
    const name = newAllocation?.name ?? getUserNameForAllocation(allocation)
    const firstName = name?.split(' ').slice(0, -1).join(' ') ?? ''
    const lastName = name?.split(' ').slice(-1).join(' ') ?? ''
    const referenceUserId = allocation.userId
    setAddContactValues(!firstName && lastName ? { firstName: lastName, referenceUserId: referenceUserId } : { firstName, lastName, referenceUserId })
    setShowAddContactModal(true)
  }

  const getAllocationId = (allocation: Allocation) => allocation.transcriptDesignation + '.' + allocation.userId

  const getUserNameForAllocation = (allocation: Allocation) => {
    const { name: allocationName, userId: allocationUserId, transcriptDesignation } = allocation
    if (!roomSession?.users) {
      return allocationName
    }
    const user = roomSession.users.find((u) => u.id === allocationUserId || u.id === transcriptDesignation)
    if (user) {
      return getUserDisplayName(user)
    }
    return allocationName
  }

  const getSpeakingPercentageForAllocation = (allocation: Allocation): number | null => {
    if (rawSpeakingDistribution === null) {
      return null
    }
    if (!allocation.transcriptDesignation) {
      return 0
    }
    const distribution = Math.round(100 * rawSpeakingDistribution[allocation.transcriptDesignation])
    return isNaN(distribution) ? 0 : distribution
  }

  const getUserTypeForAllocation = (allocation: Allocation): string => {
    const { userId: allocationUserId, userType: allocationUserType, transcriptDesignation } = allocation
    if (!roomSession?.users) {
      return allocationUserType ?? 'anonymous'
    }
    const user = roomSession.users.find((u) => u.id === allocationUserId || u.id === transcriptDesignation)
    return user?.type ?? allocationUserType ?? 'anonymous'
  }

  const getNewAllocation = (allocation: Allocation): Allocation | undefined => {
    return newAllocations.find((newAll) => getAllocationId(newAll) === getAllocationId(allocation))
  }

  const getAnonymousUserInputValue = (allocation: Allocation) => {
    const newAllocation = getNewAllocation(allocation)
    if (!newAllocation) {
      return ''
    }
    return newAllocation.name ?? getUserNameForAllocation(newAllocation) ?? ''
  }

  const handleAnonymousUserNameChanged = (allocation: Allocation, newName: string) => {
    setNewAllocations((newAllocations) =>
      newAllocations.map((newAllocation) =>
        getAllocationId(newAllocation) !== getAllocationId(allocation)
          ? newAllocation
          : { ...newAllocation, userType: 'anonymous', name: newName, email: null, roles: [] },
      ),
    )
  }

  const getModalBody = () => {
    if (userAllocationError) {
      return <div>Error getting current allocation</div>
    }

    if (userAllocationLoading || !allocations) {
      return (
        <div className='d-flex align-items-center justify-content-center'>
          <Spinner />
        </div>
      )
    }

    return (
      <Container className='d-flex flex-column zocks-gap-2'>
        <AddContactModal
          show={showAddContactModal}
          onSave={handleAddContactModalSave}
          onCancel={handleAddContactModalCancel}
          initialValues={addContactValues}
        />
        <Row className='p-0 d-flex align-items-center'>
          <Col>
            <AlertContainer>
              <i className='bi bi-exclamation-circle-fill' />
              <div className='paragraph-medium'>Saving this will recalculate all of your results on this meeting</div>
            </AlertContainer>
          </Col>
        </Row>
        <Row className='gy-4'>
          <Col xs={12} lg>
            <div className='label-small zocks-mb-1.25'>Detected participants</div>
            <Container className='d-flex flex-column zocks-gap-1.25'>
              {allocations.map((a) => {
                const name = getUserNameForAllocation(a)
                const speakingPercentage = getSpeakingPercentageForAllocation(a)
                const userType = currentUserId === a.userId ? 'current' : getUserTypeForAllocation(a)
                const newAllocation = getNewAllocation(a)
                const contactCreated = contactCreatedForAllocations.some((all) => all.transcriptDesignation === a.transcriptDesignation)
                const contactCreatedLoading = a.transcriptDesignation === contactCreatedLoadingForAllocation?.transcriptDesignation

                return (
                  <Row key={getAllocationId(a)} className='align-items-center allocated-user-container participant-row'>
                    <Col className='d-flex justify-content-between p-0 gap-2'>
                      <Col className='p-0 d-flex align-items-center gap-2'>
                        {userType === 'anonymous' ? (
                          <>
                            <AnonymousInput
                              value={getAnonymousUserInputValue(a)}
                              onChange={(e) => handleAnonymousUserNameChanged(a, e.target.value)}
                              disabled={contactCreatedLoading || contactCreated}
                              invalid={newAllocation && newAllocation.name !== null && !newAllocation.name}
                            />
                          </>
                        ) : (
                          <div className='paragraph-medium bold'>{name}</div>
                        )}
                        <UserTag userType={userType} />
                        {speakingPercentage !== null && <SpeakingDistributionIcon userType={userType} percentage={speakingPercentage} />}
                      </Col>
                      {userType === 'anonymous' ? (
                        <Col className='p-0 mt-2 mt-md-0' md='auto' xs={12}>
                          <ScopedBlock
                            scope={{
                              scope: 'ext_user:create',
                              superScope: ['ext_user:admin'],
                            }}
                            missingScopeAction={MissingScopeAction.HIDE}
                          >
                            <Button
                              className='button-small px-2 py-1'
                              variant='primary'
                              onClick={() => handleAddContactClicked(a)}
                              loading={contactCreatedLoading}
                              disabled={contactCreated}
                            >
                              {contactCreated && !contactCreatedLoading ? <i className='bi bi-check-circle' /> : <i className='bi bi-plus' />}
                              {contactCreated && !contactCreatedLoading ? 'Added to contacts' : 'Add to contacts'}
                            </Button>
                          </ScopedBlock>
                        </Col>
                      ) : null}
                      <ContactInformationButton
                        onClick={executeContactInformation}
                        loading={contactInformationIsLoading}
                        contactInformation={contactInformationByUserId?.[a.userId || a.transcriptDesignation!]}
                      />
                    </Col>
                  </Row>
                )
              })}
            </Container>
          </Col>
          <Col>
            <div className='label-small zocks-mb-1.25'>Assigned participants</div>
            <Container className='d-flex flex-column zocks-gap-1.25'>
              {newAllocations.map((a) => {
                return (
                  <Row key={getAllocationId(a)} className='align-items-center participant-row'>
                    <Col className='p-0'>
                      <UserAllocationDropdown allocation={a} onAllocationUserSelected={onAllocationUserSelected} roomId={roomId} />
                    </Col>
                  </Row>
                )
              })}
            </Container>
          </Col>
        </Row>
      </Container>
    )
  }

  return (
    <Modal
      show={show}
      onHide={onCancelNewAllocation}
      size='xl'
      header={modalHeader}
      body={getModalBody()}
      showFooterActions={true}
      onFooterPrimaryAction={saveNewAllocation}
      footerPrimaryActionDisabled={allocationExistsWithEmptyName}
      onFooterSecondaryAction={onCancelNewAllocation}
      footerPrimaryActionLoading={updateUserAllocationLoading}
    />
  )
}

export const UserAllocationDropdown: React.FC<{
  allocation: Allocation
  onAllocationUserSelected: (allocation: Allocation, selectedUser?: User) => void
  roomId: string
}> = ({ allocation, onAllocationUserSelected, roomId }) => {
  const accountId = useAppSelector(selectAccountId)
  const { id: currentUserId } = useAppSelector(selectSafeAuthenticatedUser)

  const [searchInput, setSearchInput] = useState<string>('')
  const [userFilterValues, setUserFilterValues] = useState<SelectOption[]>([])
  const [selectedOption, setSelectedOption] = useState<SelectOption | null>({ id: allocation.userId!, label: allocation.name! })
  const { data: users } = useGetUsersQuery({ accountId, name: searchInput, roomId, pageRequest: { page: 0, size: 500 } }, { refetchOnMountOrArgChange: true })

  useEffect(() => {
    if (!users || !allocation) {
      return
    }

    const userValues = users.data.map((u) => ({ id: u.id, label: u.name || getUserDisplayName(u) }))
    setUserFilterValues(userValues)
  }, [users, allocation])

  const handleSearchInputChange = async (searchInput: string) => {
    setSearchInput(searchInput)
  }
  const handleOptionSelected = (option: SelectOption) => {
    setSelectedOption(option)
    onAllocationUserSelected(
      allocation,
      (users?.data ?? []).find((u) => u.id === option.id),
    )
  }

  const AllocationUserDropdownItemOption: React.FC<DropDownItemOptionComponentProps> = useCallback(
    ({ optionId, optionLabel, onClick }) => {
      const userType = optionId === currentUserId ? 'current' : users?.data.find((u) => u.id === optionId)?.type

      return (
        <CustomDropdownItemOption role='button' onClick={onClick} className='d-flex gap-2 align-items-center text-truncate'>
          <UserTag userType={userType} className='user-tag-small' withoutStyle />
          {optionLabel}
        </CustomDropdownItemOption>
      )
    },
    [users, currentUserId],
  )

  return (
    <SelectDropdown
      initialSelectedOption={selectedOption}
      options={userFilterValues}
      className='py-2 zocks-px-0.75 zocks-gap-3'
      onOptionSelected={handleOptionSelected}
      placeholder='Select User'
      DropDownItemOptionComponent={AllocationUserDropdownItemOption}
      searchable
      onSearchInputChange={handleSearchInputChange}
    />
  )
}

interface AnonymousInputProps {
  value: string
  onChange: React.ChangeEventHandler<HTMLInputElement>
  disabled?: boolean
  invalid?: boolean
}

const AnonymousInput: React.FC<AnonymousInputProps> = ({ value, onChange, disabled, invalid }) => {
  const [inputRef, setInputFocus] = useFocus<HTMLInputElement>()

  return (
    <>
      <Button variant='primaryGhost' className='button-small p-1 border-0' onClick={setInputFocus}>
        <i className='bi bi-pencil' />
      </Button>

      <Form.Control
        ref={inputRef}
        className={classNames('border-0 p-0 anonymous-user-input', { 'anonymous-user-input-invalid': invalid })}
        type='text'
        value={value}
        onChange={onChange}
        disabled={disabled}
      />
    </>
  )
}

interface ContactInformationButtonProps {
  onClick: () => void
  loading: boolean
  contactInformation?: ContactInformation
}

const ContactInformationButton: React.FC<ContactInformationButtonProps> = ({ onClick, loading, contactInformation }) => {
  const [show, setShow] = useState(false)
  const handleOnMouseEnter = () => {
    setShow(true)
  }
  const handleOnMouseLeave = () => {
    setShow(false)
  }

  const renderTooltip = (props: OverlayInjectedProps) => (
    <BootstrapTooltip className='contact-information-tooltip' {...props} onMouseEnter={handleOnMouseEnter} onMouseLeave={handleOnMouseLeave}>
      {contactInformation ? (
        <div className='text-start m-2'>
          <ZocksAIAssistantTagVertical />
          <hr />
          {contactInformation?.identity && <span>{contactInformation.identity}</span>}
          {contactInformation?.quotes && contactInformation.quotes?.length > 0 && (
            <>
              <hr />
              <div>
                {contactInformation.quotes.map((q) => (
                  <p>
                    <i>- {q}</i>
                  </p>
                ))}
              </div>
            </>
          )}
        </div>
      ) : (
        'Click on the button to get contact information'
      )}
    </BootstrapTooltip>
  )
  return (
    <OverlayTrigger placement='right' show={show && !loading} overlay={renderTooltip}>
      <div className='contact-information-wrapper d-flex align-items-center' onMouseEnter={handleOnMouseEnter} onMouseLeave={handleOnMouseLeave}>
        <ScopedBlock
          scope={{
            scope: 'ai_agent:execute',
            superScope: ['ai_agent:admin'],
          }}
          quota={{
            quota: 'ai.agent.value',
          }}
          missingScopeAction={MissingScopeAction.HIDE}
        >
          <div
            className='cursor-pointer'
            onClick={() => {
              if (!loading) {
                onClick()
              }
            }}
          >
            {loading ? <Spinner size='sm' /> : <i className='bi bi-info-circle fs-5' />}
          </div>
        </ScopedBlock>
      </div>
    </OverlayTrigger>
  )
}
